// components/AnimatedCanvas.js
import React, { useEffect, useRef } from 'react';

const AnimatedCanvas = () => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        // Animation settings (copied from your CodePen)
        const settings = {
            pointDensity: 15,
            connections: 2,
            sizeVariation: 0.3,
            velocity: 0.00003,
            maxMovement: 50,
            attractionRange: 50,
            attractionFactor: 0.06,
            imagePath: 'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiIHN0YW5kYWxvbmU9Im5vIj8+PHN2ZyB3aWR0aD0iMjBweCIgaGVpZ2h0PSIxOHB4IiB2aWV3Qm94PSIwIDAgMjAgMTgiIHZlcnNpb249IjEuMSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4bWxuczp4bGluaz0iaHR0cDovL3d3dy53My5vcmcvMTk5OS94bGluayI+ICAgICAgICA8dGl0bGU+R3JvdXA8L3RpdGxlPiAgICA8ZGVzYz5DcmVhdGVkIHdpdGggU2tldGNoLjwvZGVzYz4gICAgPGRlZnM+PC9kZWZzPiAgICA8ZyBpZD0iUGFnZS0xIiBzdHJva2U9Im5vbmUiIHN0cm9rZS13aWR0aD0iMSIgZmlsbD0ibm9uZSIgZmlsbC1ydWxlPSJldmVub2RkIj4gICAgICAgIDxnIGlkPSJHcm91cCIgdHJhbnNmb3JtPSJ0cmFuc2xhdGUoMTAuMDAwMDAwLCA5LjAwMDAwMCkgcm90YXRlKC0zMzAuMDAwMDAwKSB0cmFuc2xhdGUoLTEwLjAwMDAwMCwgLTkuMDAwMDAwKSB0cmFuc2xhdGUoMC4wMDAwMDAsIC0xLjAwMDAwMCkiPiAgICAgICAgICAgIDxwb2x5Z29uIGlkPSJQb2x5Z29uIiBmaWxsPSIjRkZGRkZGIiBwb2ludHM9IjEwIC00LjA2MDI0NDJlLTE0IDE4LjY2MDI1NCA1IDE4LjY2MDI1NCAxNSAxMCAyMCAxLjMzOTc0NTk2IDE1IDEuMzM5NzQ1OTYgNSI+PC9wb2x5Z29uPiAgICAgICAgICAgIDxwb2x5Z29uIGlkPSJQb2x5Z29uIiBmaWxsPSIjRjBGMEYwIiBwb2ludHM9IjEwIDEuNjI0MDk3NjhlLTE0IDE4LjY2MDI1NCA1IDE4LjY2MDI1NCAxNSAxMCAyMCAxLjMzOTc0NTk2IDE1IDkuOTk5OTk5NzYgMTAiPjwvcG9seWdvbj4gICAgICAgICAgICA8cG9seWdvbiBpZD0iUG9seWdvbiIgZmlsbD0iI0U3RTdFNyIgcG9pbnRzPSIxOC42NjAyNTQgMTUgMTAgMjAgMS4zMzk3NDU5NiAxNSA5Ljk5OTk5OTc2IDEwIj48L3BvbHlnb24+ICAgICAgICA8L2c+ICAgIDwvZz48L3N2Zz4=',
            imgWidth: 10,
            imgHeight: 8,
            lineColor: "rgba(255,255,255,0.4)",
            particleDensity: 0.3,
            particleChance: 0.05,
            particleVelocity: 70,
            particleColor: "rgba(255,255,255,0.8)",
            particleLength: 10,
            flashRadius: 20,
            flashOpacity: 0.6,
            flashDecay: 0.2
        };

        const img = new Image();
        img.src = settings.imagePath;

        // Variables for animation
        let start = null;
        let lastTimestamp = null;
        let points = [];
        let particles = [];
        let mousePoint = { x: 0, y: 0 };

        // Set the canvas size to match its parent container
        const resizeCanvas = () => {
            canvas.width = canvas.parentElement.clientWidth;
            canvas.height = canvas.parentElement.clientHeight;
            createPoints();
            drawFrame();
        };

        window.addEventListener('resize', resizeCanvas);
        resizeCanvas();

        // Create animation “points” based on canvas size.
        function createPoints() {
            points = [];
            particles = [];
            for (let x = -100; x < canvas.width + 100; x += 1000 / settings.pointDensity) {
                for (let y = -100; y < canvas.height + 100; y += 1000 / settings.pointDensity) {
                    const px = Math.floor(x + Math.random() * (1000 / settings.pointDensity));
                    const py = Math.floor(y + Math.random() * (1000 / settings.pointDensity));
                    const pSizeMod = Math.random() * settings.sizeVariation + 1;
                    const pw = settings.imgWidth * pSizeMod;
                    const ph = settings.imgHeight * pSizeMod;
                    const pAnimOffset = Math.random() * 2 * Math.PI;
                    const p = {
                        x: px,
                        originX: px,
                        y: py,
                        originY: py,
                        w: pw,
                        h: ph,
                        sizeMod: pSizeMod,
                        animOffset: pAnimOffset,
                        closest: [],
                        flashOpacity: 0
                    };
                    points.push(p);
                }
            }

            // For each point, find the closest points for drawing connection lines.
            points.forEach(p1 => {
                const closest = [];
                points.forEach(p2 => {
                    if (p1 === p2) return;
                    let placed = false;
                    for (let k = 0; k < settings.connections; k++) {
                        if (!placed && !closest[k]) {
                            closest[k] = p2;
                            placed = true;
                        }
                    }
                    for (let k = 0; k < settings.connections; k++) {
                        if (!placed && getDistance(p1, p2) < getDistance(p1, closest[k])) {
                            closest[k] = p2;
                            placed = true;
                        }
                    }
                });
                p1.closest = closest;
            });
        }

        // Main animation loop using requestAnimationFrame.
        function animate(timestamp) {
            if (!start) {
                start = timestamp;
                lastTimestamp = timestamp;
            }
            const elapsed = timestamp - start;
            const delta = (timestamp - lastTimestamp) / 100;
            lastTimestamp = timestamp;

            // Update positions of the points.
            points.forEach(point => {
                const distanceToMouse = getDistance({ x: point.originX, y: point.originY }, mousePoint);
                let attractionOffset = { x: 0, y: 0 };
                if (distanceToMouse <= settings.attractionRange) {
                    const displacementFactor =
                        ((Math.cos((distanceToMouse / settings.attractionRange) * Math.PI) + 1) / 2) *
                        settings.attractionFactor;
                    attractionOffset.x = displacementFactor * (mousePoint.x - point.x);
                    attractionOffset.y = displacementFactor * (mousePoint.y - point.y);
                }
                point.x =
                    point.originX +
                    Math.sin(elapsed * settings.velocity + point.animOffset) * settings.maxMovement * point.sizeMod +
                    attractionOffset.x;
                point.y =
                    point.originY -
                    Math.cos(elapsed * settings.velocity + point.animOffset) * settings.maxMovement * point.sizeMod +
                    attractionOffset.y;
                point.flashOpacity = Math.max(0, point.flashOpacity - settings.flashDecay * delta);
            });

            // Update particles
            particles.forEach((particle, i) => {
                const origin = points[particle.origin];
                const target = origin.closest[particle.target];
                const distance = getDistance({ x: origin.x, y: origin.y }, { x: target.x, y: target.y });
                const direction = { x: (target.x - origin.x) / distance, y: (target.y - origin.y) / distance };

                particle.traveled += settings.particleVelocity * delta;
                particle.direction = direction;
                particle.x = origin.x + direction.x * particle.traveled;
                particle.y = origin.y + direction.y * particle.traveled;

                if (!isBetween(origin, { x: particle.x }, target)) {
                    particles.splice(i, 1);
                }
            });

            // Spawn new particles occasionally.
            for (let i = 0; i < settings.particleDensity * points.length; i++) {
                if (Math.random() < settings.particleChance * delta) {
                    const pOriginNum = Math.floor(Math.random() * points.length);
                    const pOrigin = points[pOriginNum];
                    const pTargetNum = Math.floor(Math.random() * pOrigin.closest.length);
                    const p = {
                        origin: pOriginNum,
                        target: pTargetNum,
                        x: pOrigin.x,
                        y: pOrigin.y,
                        traveled: 0,
                        direction: { x: 0, y: 0 }
                    };
                    particles.push(p);
                    pOrigin.flashOpacity = settings.flashOpacity;
                }
            }

            drawFrame();
            window.requestAnimationFrame(animate);
        }

        // Draw everything on the canvas.
        function drawFrame() {
            // 1. Fill the entire canvas with blue
            ctx.fillStyle = '#001738'; // background blue
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            // 2. Save state and set clipping region to exclude the left 230px.
            ctx.save();
            ctx.beginPath();
            ctx.rect(230, 0, canvas.width - 230, canvas.height);
            ctx.clip();

            // 3. Draw the animated elements (neurons) ONLY in the clipped region.

            // Draw connecting lines between points.
            points.forEach(point => {
                point.closest.forEach(cPoint => {
                    ctx.beginPath();
                    ctx.moveTo(point.x, point.y);
                    ctx.lineTo(cPoint.x, cPoint.y);
                    ctx.strokeStyle = settings.lineColor;
                    ctx.stroke();
                });
            });

            // Draw particles.
            particles.forEach(particle => {
                ctx.beginPath();
                ctx.moveTo(particle.x, particle.y);
                ctx.lineTo(
                    particle.x - particle.direction.x * settings.particleLength,
                    particle.y - particle.direction.y * settings.particleLength
                );
                ctx.strokeStyle = settings.particleColor;
                ctx.stroke();
            });

            // Draw points and flashing effects.
            points.forEach(point => {
                if (point.flashOpacity > 0) {
                    ctx.beginPath();
                    ctx.rect(
                        point.x - settings.flashRadius,
                        point.y - settings.flashRadius,
                        settings.flashRadius * 2,
                        settings.flashRadius * 2
                    );
                    const gradient = ctx.createRadialGradient(point.x, point.y, settings.flashRadius, point.x, point.y, 1);
                    gradient.addColorStop(0, "rgba(255, 255, 255, 0)");
                    gradient.addColorStop(1, "rgba(255, 255, 255, " + point.flashOpacity + ")");
                    ctx.fillStyle = gradient;
                    ctx.fill();
                }
                ctx.drawImage(img, point.x - point.w / 2, point.y - point.h / 2, point.w, point.h);
            });

            // 4. Restore the canvas to remove the clipping region.
            ctx.restore();
        }


        // Helper functions.
        function getDistance(p1, p2) {
            return Math.sqrt(Math.pow(p1.x - p2.x, 2) + Math.pow(p1.y - p2.y, 2));
        }

        function isBetween(p1, p2, t) {
            return (p1.x - p2.x) * (p2.x - t.x) > 0;
        }

        // Track mouse movements on the canvas.
        const handleMouseMove = (event) => {
            const rect = canvas.getBoundingClientRect();
            mousePoint.x = event.clientX - rect.left;
            mousePoint.y = event.clientY - rect.top;
        };

        canvas.addEventListener('mousemove', handleMouseMove);
        window.requestAnimationFrame(animate);

        // Cleanup listeners on component unmount.
        return () => {
            window.removeEventListener('resize', resizeCanvas);
            canvas.removeEventListener('mousemove', handleMouseMove);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                backgroundColor: '#001738' // Change this to your preferred color
            }}
        />
    );
};

export default AnimatedCanvas;
